import type { FC } from 'react';
import Header from '@/components/UI/Header';
import { StudyStatus } from '@/components/UI/StudyStatusIndicator/StudyStatusIndicator';
import { _isNotBlank, _isNotEmpty } from '@/littledash';
import type { Approval } from '@/model/Approval.model';
import type { Study } from '@/model/Study.model';
import { renderStudyClosureDate } from '../StudyClosureDate.utils';
import StudyUsers from '../Users';

interface StudyHeaderV2Props {
  study: Study;
  approvals?: Array<Pick<Approval, 'status'>>;
}

interface StudyStatusIndicatorData {
  status: StudyStatus;
  date?: string;
  user?: string;
}

const PENDING_REVIEW_STATUSES = new Set(['pending', 'awaiting_approval']);

// A pending review outranks an unsubmitted one. Review required only applies when nothing is
// waiting on approval.
const reviewStatus = (approvals: Array<Pick<Approval, 'status'>>): StudyStatus | undefined => {
  if (!_isNotEmpty(approvals)) {
    return undefined;
  }

  if (approvals.some((approval) => PENDING_REVIEW_STATUSES.has(approval.status))) {
    return StudyStatus.reviewInProgress;
  }

  if (approvals.some((approval) => approval.status === 'not_submitted')) {
    return StudyStatus.reviewUnsubmitted;
  }

  return undefined;
};

const studyStatusIndicator = (
  study: Study,
  approvals: Array<Pick<Approval, 'status'>>
): StudyStatusIndicatorData | undefined => {
  const timezone = study.config?.timezone;

  if (_isNotBlank(study.canceled_at)) {
    return {
      status: StudyStatus.cancelled,
      date: renderStudyClosureDate(study.canceled_on, study.canceled_at, timezone),
    };
  }

  if (_isNotBlank(study.archived_at)) {
    return {
      status: StudyStatus.completed,
      date: renderStudyClosureDate(study.completed_on, study.archived_at, timezone),
    };
  }

  const statusFromReviews = reviewStatus(approvals);
  if (statusFromReviews === StudyStatus.reviewInProgress || statusFromReviews === StudyStatus.reviewUnsubmitted) {
    return { status: statusFromReviews };
  }

  switch (study.status) {
    case 'rejected':
    case 'fail':
      return { status: StudyStatus.rejected };
    case 'awaiting_approval':
      return { status: StudyStatus.reviewInProgress };
    case 'not_submitted':
      return { status: StudyStatus.reviewUnsubmitted };
    case 'in_progress':
      return { status: StudyStatus.inProgress };
    case 'success':
    case 'approved':
      return { status: StudyStatus.completed };
    case 'pending':
      return undefined;
  }
};

const StudyHeaderV2: FC<StudyHeaderV2Props> = ({ study, approvals = [] }) => {
  const statusIndicator = studyStatusIndicator(study, approvals);
  return (
    <div className="ph4">
      <Header
        mainHeaderText={study.name}
        subHeaderText={study.code}
        asideComponent={() => <StudyUsers study={study} customStyle={{ whiteSpace: 'nowrap' }} />}
        studyStatus={statusIndicator?.status}
        studyStatusDate={statusIndicator?.date}
        studyStatusUser={statusIndicator?.user}
        className="flex-nowrap"
      />
    </div>
  );
};

export default StudyHeaderV2;
